import { useCallback } from 'react';
import { TExcludedDate } from '@timeedit/types/lib/types';
import { Button, Icon, TEInput } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import { DateRangePicker } from './DateRangePicker';

type ExcludeDatesProps = {
  excludedDates?: TExcludedDate[];
  onChange: (excludedDates: TExcludedDate[]) => void;
  timezone: string;
};
export const ExcludedDates = ({
  excludedDates,
  onChange,
  timezone,
}: ExcludeDatesProps) => {
  const onAdd = useCallback(() => {
    const newValue: TExcludedDate[] = [
      ...(excludedDates || []),
      [undefined, undefined],
    ];
    onChange(newValue);
  }, [excludedDates, onChange]);

  const onDelete = useCallback(
    (index: number) => {
      if (!excludedDates) return;

      const newExcludedDates = [...excludedDates];
      newExcludedDates.splice(index, 1);
      onChange(newExcludedDates);
    },
    [excludedDates, onChange]
  );

  const handleChange = useCallback(
    (index: number, excludedDate: TExcludedDate) => {
      if (!excludedDates) return;
      const newExcludedDates = [...excludedDates];
      newExcludedDates[index] = excludedDate;
      onChange(newExcludedDates);
    },
    [excludedDates, onChange]
  );

  return (
    <TEInput.LabelWrapper
      label="Exclude dates"
      tooltip="Will exclude reservations in these excluded dates"
    >
      <Space direction="vertical" style={{ marginBottom: 3 }}>
        <Space
          direction="vertical"
          size="small"
          style={{ marginTop: excludedDates?.length ? 6 : 0 }}
        >
          {excludedDates?.map((excludedDate, index) => (
            <ExcludedDateInput
              key={index}
              excludedDate={excludedDate}
              onChange={(excludedDate) => handleChange(index, excludedDate)}
              timezone={timezone}
              onDelete={() => onDelete(index)}
            />
          ))}
        </Space>
        <Button size="small" type="dashed" onClick={onAdd}>
          Add
        </Button>
      </Space>
    </TEInput.LabelWrapper>
  );
};

type ExcludedDateInputProps = {
  excludedDate?: TExcludedDate;
  onChange: (excludedDate: TExcludedDate) => void;
  timezone: string;
  onDelete: () => void;
};
const ExcludedDateInput = ({
  excludedDate,
  onChange,
  timezone,
  onDelete,
}: ExcludedDateInputProps) => {
  return (
    <Space>
      <DateRangePicker
        value={{ dateStart: excludedDate?.[0], dateEnd: excludedDate?.[1] }}
        onChange={(dateStrings) => {
          onChange([dateStrings.dateStart, dateStrings.dateEnd]);
        }}
        timezone={timezone}
      />
      <Button
        size="small"
        icon={<Icon id="DeleteOutlined" alt="Delete" />}
        onClick={onDelete}
      ></Button>
    </Space>
  );
};
